import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import * as Dialog from "@radix-ui/react-dialog";
import toast from "react-hot-toast";
import type { Document } from "../../lib/types";
import { patchEntityDocument } from "../../lib/dataFetchers";

interface EditDocumentDialogProps {
  document: Document;
  entityId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditDocumentDialog({
  document,
  entityId,
  open,
  onOpenChange,
}: EditDocumentDialogProps): JSX.Element {
  const { t } = useTranslation("documents");
  const qc = useQueryClient();

  const [name, setName] = useState(document.name);
  const [notes, setNotes] = useState(document.notes ?? "");
  const [nameError, setNameError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  // Prefill / reset when dialog opens with a (possibly new) document
  useEffect(() => {
    if (open) {
      setName(document.name);
      setNotes(document.notes ?? "");
      setNameError(null);
      // Focus the name field on open (a11y — first interactive element).
      // setTimeout defers by one tick to allow Radix's focus-trap to settle first.
      setTimeout(() => nameRef.current?.focus(), 0);
    }
  }, [open, document]);

  const mutation = useMutation({
    mutationFn: () =>
      patchEntityDocument(entityId, document.id, {
        name: name.trim(),
        notes: notes.trim() || null,
        comment: null,
        expectedUpdatedAt: document.updatedAt,
      }),
    onSuccess: () => {
      toast.success(t("edit_success_toast"));
      void qc.invalidateQueries({ queryKey: ["entity-documents", entityId] });
      void qc.invalidateQueries({ queryKey: ["entity", entityId] });
      void qc.invalidateQueries({ queryKey: ["entities"] });
      void qc.invalidateQueries({ queryKey: ["renewals"] });
      onOpenChange(false);
    },
    onError: (err: unknown) => {
      const status =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { status?: number } }).response?.status
          : undefined;
      if (status === 409) {
        toast.error(t("edit_conflict_toast"));
        void qc.invalidateQueries({ queryKey: ["entity-documents", entityId] });
        onOpenChange(false);
      } else {
        toast.error(t("edit_error_toast"));
        // Dialog stays open — operator can correct inline errors
      }
    },
  });

  function validate(): boolean {
    if (!name.trim()) {
      setNameError(t("upload_validation_name_required"));
      return false;
    }
    setNameError(null);
    return true;
  }

  function handleSubmit() {
    if (validate()) {
      mutation.mutate();
    }
  }

  function handleKeyDown(e: KeyboardEvent<HTMLElement>) {
    // ESC is handled natively by Radix Dialog (fires onOpenChange(false))
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 z-40" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50
                     w-full max-w-md bg-surface rounded-xl shadow-xl p-6
                     focus:outline-none"
          onKeyDown={handleKeyDown}
          aria-describedby={undefined}
        >
          <Dialog.Title className="text-base font-semibold text-text mb-4">
            {t("edit_dialog_title")}
          </Dialog.Title>

          <div className="space-y-4">
            {/* Name */}
            <div>
              <label
                htmlFor="edit-doc-name"
                className="block text-xs font-medium text-text-muted mb-1"
              >
                {t("upload_field_name")}
                <span className="text-sev-expired ml-1" aria-hidden>
                  *
                </span>
              </label>
              <input
                id="edit-doc-name"
                ref={nameRef}
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full text-base rounded-md border border-border bg-bg
                           px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent/30"
                aria-invalid={nameError !== null}
                aria-describedby={nameError ? "edit-name-error" : undefined}
                disabled={mutation.isPending}
              />
              {nameError && (
                <p id="edit-name-error" role="alert" className="mt-1 text-xs text-sev-expired">
                  {nameError}
                </p>
              )}
            </div>

            {/* Notes */}
            <div>
              <label
                htmlFor="edit-doc-notes"
                className="block text-xs font-medium text-text-muted mb-1"
              >
                {t("upload_field_notes")}
              </label>
              <textarea
                id="edit-doc-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full text-base rounded-md border border-border bg-bg
                           px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent/30
                           resize-none"
                disabled={mutation.isPending}
              />
            </div>

          </div>

          {/* Footer — mobile: stacked; sm+: row-reverse (primary on right) */}
          <div className="flex flex-col sm:flex-row-reverse gap-2 mt-6">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={mutation.isPending}
              className="inline-flex items-center justify-center px-4 py-2 rounded-md
                         text-sm font-medium bg-accent text-white hover:bg-accent/90
                         min-h-[44px] focus:outline-none focus:ring-2 focus:ring-accent/30
                         disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {mutation.isPending ? t("upload_submitting_button") : t("edit_save")}
            </button>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
              className="inline-flex items-center justify-center px-4 py-2 rounded-md
                         text-sm font-medium border border-border text-text hover:bg-bg
                         min-h-[44px] focus:outline-none focus:ring-2 focus:ring-accent/30
                         disabled:opacity-50"
            >
              {t("edit_cancel")}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
